<template>
  <BaseForm
    :title="formTitle"
    :formData="jobData"
    :fields="fields"
    :submitUrl="submitUrl"
    :method="method"
    :selectedYear="selectedYear"
    :dateRestriction="restrictDates"
    @form-success="handleSuccess"
    @form-error="handleError"
    @form-changed="onFormChanged"
  />
</template>

<script>
import BaseForm from './BaseForm.vue';
import axios from 'axios';
import { selectedYear } from '../store/store'

export default {
  components: {
    BaseForm
  },
  setup() {
    const restrictDates = (date) => {
      let year = selectedYear.value;
      const start = `${year}/01/01`;
      const end = `${year}/12/31`;
      return date >= start && date <= end;
    };

    return {
      selectedYear,
      restrictDates,
    };
  },
  props: {
    jobId: {
      type: Number,
      default: null
    }
  },
  data() {
    return {
      jobData: {
        date: '',
        customer_id: '',
        producer_id: '',
        crop_id: '',
        field_id: '',
        bag_pit_id: '',
        moisture: ''
      },
      customerBagPitMap: {}, // Holds the mapping of customers to bag pits
      bagPitOptions: [], // Holds the dynamically filtered bag pit options
      currentCustomer: '', // For updating bag pit options
      producerFieldMap: {},
      fieldOptions: [],
      currentProducer: '', // For updating producer field options
      fields: [
        { model: 'date', label: 'Date', type: 'datetime', required: true },
        { model: 'customer_id', label: 'Customer', required: true, type: 'select', optionsUrl: '/api/v1/customer/customers/', labelField: 'name' },
        { model: 'producer_id', label: 'Producer', required: true, type: 'select', optionsUrl: '/api/v1/producer/producers/', labelField: 'name' },
        { model: 'crop_id', label: 'Crop', required: true, type: 'select', optionsUrl: '/api/v1/crop/crops/', labelField: 'crop' },
        {
          model: 'field_id',
          label: 'Field',
          required: true,
          type: 'select',
          options: () => this.fieldOptions,
          labelField: 'name',
          hint: (formData) => !formData.producer_id ? '* select Producer to enable fields' : ''
        },
        {
          model: 'bag_pit_id',
          label: 'Bag Pit',
          type: 'select',
          options: () => this.bagPitOptions,
          hint: (formData) => !formData.customer_id ? '* select Customer to enable fields' : '',
          required: false
        },
        { model: 'moisture', label: 'Moisture (%)', required: true, type: 'number' }
      ]
    };
  },
  computed: {
    formTitle() {
      return this.jobId ? `Edit Job` : 'Add Job';
    },
    submitUrl() {
      return this.jobId ? `/api/v1/job/jobs/${this.jobId}/` : '/api/v1/job/jobs/';
    },
    method() {
      return this.jobId ? 'put' : 'post';
    }
  },
  mounted() {
    if (this.jobId) {
      this.loadJobData();
    }
    this.fetchAllBagPits();
    this.fetchAllFields();
  },
  methods: {
    async fetchAllBagPits() {
      try {
        const response = await axios.get('/api/v1/customer_bag_pit/customer_bag_pits/');
        this.customerBagPitMap = {};

        response.data.forEach(bagPit => {
          const customerId = bagPit.customer_id.id;
          if (!this.customerBagPitMap[customerId]) {
            this.customerBagPitMap[customerId] = [];
          }

          this.customerBagPitMap[customerId].push({
            value: bagPit.id,
            label: `${bagPit.customer_id.name} - ${bagPit.name}`
          });
        });

        // If a customer is pre-selected, update bag pit options
        if (this.jobData.customer_id) {
          this.updateBagPitOptions(this.jobData.customer_id);
        }
      } catch (error) {
        console.error('Error fetching customer bag pits:', error);
      }
    },

    updateBagPitOptions(customerId) {
      this.bagPitOptions = customerId ? this.customerBagPitMap[customerId] || [] : [];
      if (this.jobData.bag_pit_id !== '') {
          this.jobData.bag_pit_id = '';
      }
    },

    async fetchAllFields() {
      try {
        const response = await axios.get('/api/v1/field/fields/');
        this.producerFieldMap = {};

        // Group fields by producer
        response.data.forEach(field => {
          const producerId = field.producer_id.id;
          if (!this.producerFieldMap[producerId]) {
            this.producerFieldMap[producerId] = [];
          }
          this.producerFieldMap[producerId].push({
            value: field.id,
            label: field.name
          });
        });

        // If a producer is already selected (like on edit), update the options
        if (this.jobData.producer_id) {
          this.updateFieldOptions(this.jobData.producer_id);
        }
      } catch (error) {
        console.error('Error fetching fields:', error);
      }
    },

    updateFieldOptions(producerId) {
      this.fieldOptions = producerId ? (this.producerFieldMap[producerId] || []) : [];
      // Reset field_id if the selected producer changed
      if (this.jobData.field_id) {
        this.jobData.field_id = '';
      }
    },

    loadJobData() {
      axios.get(`/api/v1/job/jobs/${this.jobId}/`).then(response => {
        this.jobData = {
          ...response.data,
          customer_id: response.data.customer_id.id,
          producer_id: response.data.producer_id.id,
          crop_id: response.data.crop_id.id,
          field_id: response.data.field_id.id,
          bag_pit_id: response.data.bag_pit_id ? response.data.bag_pit_id.id : ''
        }; // Adjust for nested object ids

        // Don't show edit content for jobs not in selectedYear
        const jobYear = new Date(this.jobData.date).getFullYear();

        // Compare the years and redirect if they differ
        if (jobYear !== this.selectedYear) {
          this.selectedYear = jobYear;
        }
      }).catch(console.error);
    },

    onFormChanged(newVal) {
      // This receives newVal, oldVal from the child's `form-changed` event:
      // old val not correct? oh well track it independently. 
      if (newVal.customer_id != this.currentCustomer) {
        // resync data from BaseForm. needed to trigger update 
        this.jobData = {
          ...this.jobData,
          ...newVal
        };
        this.currentCustomer = newVal.customer_id;
        this.updateBagPitOptions(this.currentCustomer);
      }

      if (newVal.producer_id != this.currentProducer) {
        this.jobData = {
          ...this.jobData,
          ...newVal
        };
        this.currentProducer = newVal.producer_id;
        this.updateFieldOptions(this.currentProducer);
      }
    },

    handleSuccess() {
      this.$q.notify({
        color: 'green',
        position: 'top',
        message: 'Job saved successfully!',
        icon: 'check_circle',
        timeout: 3000
      });
    },
    handleError(error) {
      console.error(error);
      this.$q.notify({
        color: 'red',
        position: 'top',
        message: 'There was a problem saving the job!',
        icon: 'check_circle',
        timeout: 3000
      });
    }
  }
};
</script>
